import { copilotPlanner, CopilotPlan, CopilotStep } from './copilotPlanner';
import { traceRecorder } from '../trace/traceRecorder';

export interface CopilotSession {
    id: string;
    plan: CopilotPlan;
    currentStepIndex: number;
    status: "active" | "paused" | "completed";
    createdAt: number;
    updatedAt: number;
}

let activeSession: CopilotSession | null = null;

export const copilotEngine = {
    startNewPlan(intent: string, query: string): CopilotSession {
        const plan = copilotPlanner.createPlan(intent, query);
        
        activeSession = {
            id: `SESS-${Date.now()}`,
            plan,
            currentStepIndex: 0,
            status: "active",
            createdAt: Date.now(),
            updatedAt: Date.now()
        };
        
        console.group(`%c[Copilot] Session Started: ${activeSession.id}`, 'color: #3b82f6; font-weight: bold;');
        console.info(`Plan: ${plan.title}`);
        plan.steps.forEach(s => console.info(`- ${s.label}`));
        console.groupEnd();

        traceRecorder.recordDecision(
            `Copilot: Session started - ${plan.title}`, 
            { lat: 0, lon: 0 }, 
            { sessionId: activeSession.id, steps: plan.steps.length }
        );
        
        // Execute the first step immediately
        this.executeCurrentStep();
        
        return activeSession;
    },

    executeCurrentStep() {
        if (!activeSession || activeSession.status !== "active") return;
        
        const step = activeSession.plan.steps[activeSession.currentStepIndex];
        console.info(`[Copilot] Executing Step ${activeSession.currentStepIndex + 1}: ${step.label}`);
        
        // Trigger spatial navigation (cross-module dispatch)
        if (typeof window !== 'undefined' && (window as any).WingmanSpatialControl) {
            (window as any).WingmanSpatialControl.setIntent(step.intent);
        }

        traceRecorder.recordDecision(
            `Copilot: Step ${activeSession.currentStepIndex + 1} - ${step.label}`, 
            { lat: 0, lon: 0 }, 
            { taskId: step.id, session: activeSession.id }
        );
        
        activeSession.updatedAt = Date.now();
    },

    nextStep() {
        if (!activeSession) return;
        
        const nextIdx = activeSession.currentStepIndex + 1;
        if (nextIdx >= activeSession.plan.steps.length) {
            this.completeSession();
            return;
        }

        activeSession.currentStepIndex = nextIdx;
        this.executeCurrentStep();
    },

    skipStep() {
        console.info("[Copilot] Skipping current step...");
        this.nextStep();
    },

    pauseSession() {
        if (activeSession) {
            activeSession.status = "paused";
            console.info(`[Copilot] Session ${activeSession.id} paused.`);
        }
    },

    resumeSession() {
        if (activeSession && activeSession.status === "paused") {
            activeSession.status = "active";
            console.info(`[Copilot] Session ${activeSession.id} resumed.`);
            this.executeCurrentStep();
        }
    },

    completeSession() {
        if (activeSession) {
            activeSession.status = "completed";
            console.info(`[Copilot] Plan complete: ${activeSession.plan.title}`);
            
            // Phase 12: Record to Memory (trigger hook for memory system)
            if (typeof window !== 'undefined' && (window as any).WingmanMemoryRecorder) {
                (window as any).WingmanMemoryRecorder.recordSessionOutcome(activeSession);
            }

            activeSession = null;
        }
    },

    getActiveSession() {
        return activeSession;
    }
};

if (typeof window !== 'undefined') {
    (window as any).WingmanCopilot = copilotEngine;
}
